import { useCallback, useContext, useEffect, useMemo, useRef } from "react";

import { QuoteDetailsContext } from "~/features/quote/context/quote-details-context.tsx";
import { useAddressBookAccessorials } from "~/features/quote/hooks/use-address-book-accessorials.ts";
import { useQuoteDetailsPageLoadQueries } from "~/features/quote/hooks/use-quote-details-hooks.ts";
import type { Accessorial } from "~/shared/schemas/accessorial-schema.ts";
import type { AddressBookItemSummary } from "~/shared/schemas/address-book-schema.ts";
import type { Country } from "~/shared/schemas/location-state-schema.ts";
import type {
	OnChange,
	QuoteStateType,
} from "~/shared/types/quote-details-types.ts";

export type SubGroupKey = "pickup" | "delivery";

export const RESIDENTIAL_SERVICE_NAME = "Residential";
export const APPOINTMENT_SERVICE_NAME = "Appointment Delivery";
const LOCATION_TYPE_NAMES = new Set<string>([
	"Limited Access",
	"Trade Show",
	RESIDENTIAL_SERVICE_NAME,
]);

const addLocationType = (
	currentServices: Accessorial[],
	option: Accessorial,
): Accessorial[] => {
	const servicesWithoutCommercial = currentServices.filter(
		(service) => service.name !== "Commercial",
	);
	return [...servicesWithoutCommercial, option];
};

const removeLocationType = (
	currentServices: Accessorial[],
	option: Accessorial,
	commercialOption: Accessorial | undefined,
): Accessorial[] => {
	const servicesWithoutThisLocationType = currentServices.filter(
		(service) => service.id !== option.id,
	);
	const hasOtherLocationTypes = servicesWithoutThisLocationType.some(
		(service) => LOCATION_TYPE_NAMES.has(service.name),
	);

	if (!hasOtherLocationTypes && commercialOption) {
		return [...servicesWithoutThisLocationType, commercialOption];
	}

	return servicesWithoutThisLocationType;
};

const addService = (
	currentServices: Accessorial[],
	option: Accessorial,
): Accessorial[] => {
	if (currentServices.some((service) => service.id === option.id)) {
		return currentServices;
	}

	return [...currentServices, option];
};

const removeService = (
	currentServices: Accessorial[],
	option: Accessorial,
): Accessorial[] =>
	currentServices.filter((service) => service.id !== option.id);

export const usePickupDeliveryDetails = (name: SubGroupKey) => {
	const { onChange, quoteState } = useContext(QuoteDetailsContext);
	const { data } = useQuoteDetailsPageLoadQueries();
	const countries = useMemo(() => data?.countries ?? [], [data?.countries]);

	const { applyAddressBookAccessorials } = useAddressBookAccessorials();

	const { locationType, selectable } = data?.accessorials ?? {};

	const services = useMemo(
		() => (selectable ?? {}) as Record<SubGroupKey, Accessorial[]>,
		[selectable],
	);

	const locationTypeOptions = useMemo(() => {
		if (!locationType || !name) return [];

		const options = locationType[name as keyof typeof locationType] ?? [];
		const commercial = options.find((opt) => opt.name === "Commercial");
		const others = options.filter((opt) => opt.name !== "Commercial");

		return commercial ? [commercial, ...others] : options;
	}, [locationType, name]);

	// Combine location type and services options for the checkbox field
	const combinedOptions = useMemo(() => {
		const locationTypeItems = locationTypeOptions || [];
		const serviceItems = services[name] || [];
		const allOptions = [...locationTypeItems, ...serviceItems];

		// Filter out Commercial from the displayed options
		// return allOptions.filter((option) => option.name !== "Commercial");
		return allOptions;
	}, [locationTypeOptions, services, name]);

	// Get the Commercial option for default selection
	const commercialOption = useMemo(() => {
		const locationTypeItems = locationTypeOptions || [];
		const serviceItems = services[name] || [];
		const allOptions = [...locationTypeItems, ...serviceItems];
		return allOptions.find((option) => option.name === "Commercial");
	}, [locationTypeOptions, services, name]);

	const getFieldProps = (field: string) => ({
		id: `${name}${field}`,
		name: `${name}${field}`,
		onChange: ({ value }: { value: OnChange }) => {
			onChange(`${name}${field}` as keyof QuoteStateType, value);
		},
		value: (quoteState as Record<string, unknown>)[`${name}${field}`] ?? null,
	});

	// Tracks whether Appointment Delivery was explicitly selected by the user.
	const appointmentExplicitlySelected = useRef(false);

	const normalizeServices = useCallback(
		(nextServices: Accessorial[]) => {
			if (name !== "delivery") {
				return nextServices;
			}

			const hasResidential = nextServices.some(
				(service) => service.name === RESIDENTIAL_SERVICE_NAME,
			);
			const hasAppointment = nextServices.some(
				(service) => service.name === APPOINTMENT_SERVICE_NAME,
			);

			if (hasResidential && !hasAppointment) {
				const appointmentOption = services.delivery?.find(
					(service) => service.name === APPOINTMENT_SERVICE_NAME,
				);
				return appointmentOption
					? [...nextServices, appointmentOption]
					: nextServices;
			}

			return nextServices;
		},
		[name, services],
	);

	const applyServicesChange = useCallback(
		(nextServices: Accessorial[]) => {
			onChange(
				`${name}Services` as keyof QuoteStateType,
				normalizeServices(nextServices),
			);
		},
		[name, normalizeServices, onChange],
	);

	const handleApplyAddressBookAccessorials = useCallback(
		(address: AddressBookItemSummary) => {
			applyAddressBookAccessorials(name, address);
		},
		[applyAddressBookAccessorials, name],
	);

	// Handle checkbox selection change
	const handleCombinedSelectionChange = (
		option: Accessorial,
		checked: boolean,
	) => {
		const currentServices = quoteState[`${name}Services`] ?? [];
		const isLocationTypeOption = LOCATION_TYPE_NAMES.has(option.name);

		// Prevent unchecking Appointment for delivery Residential
		if (
			name === "delivery" &&
			option.name === APPOINTMENT_SERVICE_NAME &&
			!checked &&
			currentServices.some(
				(service) => service.name === RESIDENTIAL_SERVICE_NAME,
			)
		) {
			return; // Do not allow unchecking
		}

		// Track explicit user selection of Appointment Delivery
		if (name === "delivery" && option.name === APPOINTMENT_SERVICE_NAME) {
			appointmentExplicitlySelected.current = checked;
		}

		let nextServices: Accessorial[];
		if (isLocationTypeOption && checked) {
			nextServices = addLocationType(currentServices, option);
		} else if (isLocationTypeOption) {
			nextServices = removeLocationType(
				currentServices,
				option,
				commercialOption,
			);
		} else if (checked) {
			nextServices = addService(currentServices, option);
		} else {
			nextServices = removeService(currentServices, option);
		}

		if (nextServices === currentServices) {
			return;
		}

		applyServicesChange(nextServices);
	};

	const handleRadioSelectionChange = (option: Accessorial) => {
		const currentServices = quoteState[`${name}Services`] ?? [];
		const locationTypeNames = new Set([
			"Limited Access",
			"Trade Show",
			RESIDENTIAL_SERVICE_NAME,
			"Commercial",
		]);
		// Remove other location types
		const servicesWithoutLocationTypes = currentServices.filter(
			(item) => !locationTypeNames.has(item.name),
		);

		let baseServices = servicesWithoutLocationTypes;

		const wasResidential = currentServices.some(
			(service) => service.name === RESIDENTIAL_SERVICE_NAME,
		);
		if (
			name === "delivery" &&
			wasResidential &&
			option.name !== RESIDENTIAL_SERVICE_NAME &&
			!appointmentExplicitlySelected.current
		) {
			baseServices = baseServices.filter(
				(service) => service.name !== APPOINTMENT_SERVICE_NAME,
			);
		}

		if (name === "delivery" && option.name === RESIDENTIAL_SERVICE_NAME) {
			// Appointment will be auto-added by normalizeServices;
			// preserve the explicit flag only if appointment was already explicitly selected
			const hadAppointmentExplicitly =
				appointmentExplicitlySelected.current &&
				baseServices.some(
					(service) => service.name === APPOINTMENT_SERVICE_NAME,
				);
			appointmentExplicitlySelected.current = hadAppointmentExplicitly;
		}
		applyServicesChange([...baseServices, option]);
	};

	// Check if an option is currently selected
	const isOptionSelected = (option: Accessorial): boolean => {
		const currentServices = quoteState[`${name}Services`] ?? [];
		return currentServices.some((item) => item.id === option.id);
	};

	const resetAccessorials = useCallback(() => {
		appointmentExplicitlySelected.current = false;
		if (commercialOption) {
			applyServicesChange([commercialOption]);
		} else {
			applyServicesChange([]);
		}
	}, [commercialOption, applyServicesChange]);

	// Default to United States
	useEffect(() => {
		const currentCountryKey = `${name}Country` as keyof QuoteStateType;
		const currentCountry = quoteState[currentCountryKey] as Country;
		if (countries && countries.length > 0 && currentCountry.name === "") {
			onChange(
				`${name}Country`,
				countries.find((c) => c.name === "United States"),
			);
		}
	}, [countries, name, onChange, quoteState]);

	// Default to Commercial location type (only for new quotes, not when loading existing quotes)
	useEffect(() => {
		const currentServices = quoteState[`${name}Services`] ?? [];
		const isExistingQuote = quoteState.id > 0;

		if (commercialOption && currentServices.length === 0 && !isExistingQuote) {
			applyServicesChange([commercialOption]);
		}
	}, [applyServicesChange, commercialOption, name, quoteState]);

	return {
		applyAddressBookAccessorials: handleApplyAddressBookAccessorials,
		countries,
		combinedOptions,
		getFieldProps,
		handleCombinedSelectionChange,
		handleRadioSelectionChange,
		isOptionSelected,
		onChange,
		quoteState,
		resetAccessorials,
		services,
		locationTypeOptions,
	};
};
